import React, { useCallback, useMemo } from 'react'

import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'
import {
  TtButton,
  TtCheckbox,
  TtFlavorContext,
  TtIcon,
  TtMenu,
  TtMenuDivider,
  TtMenuItem,
  TtMenuLabel,
} from '@23/react'
import { FormikHelpers, FormikProps } from 'formik'
import classNames from 'classnames'
import TtFormik from '../../../../global/formik-forms'

import {
  useIsPanelInitialized,
  usePanel,
} from '../../../../firebase/hooks/usePanel'
import { useHumanParticipants } from '../../../../webrtc/webRTCRoom'
import { useFloorState } from '../../../../firebase/hooks/useFloor'
import { useLastActiveMediaTrayCardExists } from '../../../../firebase/hooks/useTray'

import './PanelMenuForm.scss'

interface PanelMenuFormProps {
  onSubmit: (
    selectedValues: number[],
    screen: number | 'tray' | undefined
  ) => void
}

type FormValues = {
  [key: string]: boolean | number | string | undefined
}

const PanelMenuForm = ({ onSubmit }: PanelMenuFormProps) => {
  const participants = useHumanParticipants()
  const floor = useFloorState()
  const { mode, participantIds, screen } = usePanel()
  const isPanelInitialized = useIsPanelInitialized()
  const lastActiveMediaTrayCardExists = useLastActiveMediaTrayCardExists()

  const listOptions = useMemo(() => {
    if (!isPanelInitialized || !floor) return null
    return participants.map((p) => {
      const selectedIds = mode === 'manual' ? participantIds : null

      const isSelected =
        floor?.layout === 'panel' && mode === 'auto'
          ? true
          : (selectedIds?.includes(p.id) ?? true)

      return {
        label: p.name,
        value: p.id,
        isSelected,
        screenAvailable:
          p.tracks.screen &&
          p.tracks.screen.isEnabled &&
          p.tracks.screen.mediaStream,
        screenSelected: screen === p.id,
      }
    })
  }, [isPanelInitialized, floor, participants, mode, participantIds, screen])

  const initialValues: FormValues = useMemo(() => {
    return (
      listOptions?.reduce(
        (previous, newValue) => {
          return {
            ...previous,
            [`${newValue.value}`]: newValue.isSelected,
          }
        },
        { screen }
      ) ?? {}
    )
  }, [listOptions, screen])

  const onSubmitHandler = (
    values: FormValues,
    _: FormikHelpers<FormValues>
  ) => {
    const ids = Object.entries(values)
      .reduce<number[]>((selectedIds, [id]) => {
        if (id !== 'screen' && values[id]) {
          return [...selectedIds, +id]
        }
        return selectedIds
      }, [])
      .filter((id) => {
        // Check if the participant has a camera track and is enabled
        // If not, we don't want to include them in the selected ids
        const participant = participants?.find((p) => p.id === id)
        return (
          participant &&
          participant.tracks.camera &&
          participant.tracks.camera.isEnabled
        )
      })

    // We need to check if the screen being set is enabled
    let newScreenState = values.screen
    if (Number.isInteger(newScreenState)) {
      const hasScreenEnabled = participants?.find(
        (p) => p.id === newScreenState
      )?.tracks.screen?.isEnabled
      if (!hasScreenEnabled) {
        newScreenState = undefined
      }
    }

    onSubmit(ids, newScreenState as number | 'tray' | undefined)
  }

  const onUndoHandler = useCallback(
    (formik: FormikProps<FormValues>) => {
      formik.resetForm(initialValues)
    },
    [initialValues]
  )

  const toggleSelection = useCallback(
    async (formik: FormikProps<FormValues>, s: number | 'tray') => {
      await formik.setFieldValue(
        'screen',
        s === formik.values.screen ? undefined : s
      )
    },
    []
  )

  const handleTrayClick = useCallback(
    async (formik: FormikProps<FormValues>) => {
      await formik.setFieldValue(
        'screen',
        formik.values.screen === 'tray' ? undefined : 'tray'
      )
    },
    []
  )

  if (!listOptions) return null

  return (
    <TtFormik.Form<FormValues>
      label="Configure panel form"
      className="configure-panel-form"
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmitHandler}
      novalidate
    >
      {(formik) => {
        const formScreenValue = formik.values.screen

        return (
          <TtFlavorContext flavor="webinars">
            <TtMenu className="panel-form-menu">
              <TtMenuLabel className="panel-form-menu-label">
                <FormattedMessage defaultMessage="Configure Panel" />
                <TtButton
                  size="x-small"
                  label={intl.formatMessage({
                    defaultMessage: 'Undo changes',
                  })}
                  color="secondary"
                  onClick={() => onUndoHandler(formik)}
                  className={classNames('undo-changes-btn', {
                    hide: !formik.dirty,
                  })}
                >
                  <TtIcon name="undo" library="one/wysiwyg" />
                </TtButton>
                <TtFormik.SubmitButton
                  label={intl.formatMessage({ defaultMessage: 'Submit' })}
                  size="x-small"
                  color="primary"
                  disabled={false}
                  loading={false}
                >
                  <TtIcon name="check" library="one" />
                </TtFormik.SubmitButton>
              </TtMenuLabel>

              {listOptions.map((option) => {
                const participant = participants?.find(
                  (p) => p.id === option.value
                )

                const hasCamera = participant?.tracks?.camera?.isEnabled
                const hasScreen = participant?.tracks?.screen?.isEnabled

                return (
                  <div className="panel-form-item" key={option.value}>
                    <TtMenuItem
                      className="panel-form-menu-item"
                      disabled={!hasCamera}
                      onClick={() => {
                        if (participant && hasCamera) {
                          formik.setFieldValue(
                            `${participant.id}`,
                            !formik.values[participant.id]
                          )
                        }
                      }}
                    >
                      {hasCamera ? (
                        <TtCheckbox
                          label=""
                          checked={
                            formik.values[participant.id] ? 'true' : 'false'
                          }
                          onTt-change={() => {
                            formik.setFieldValue(
                              `${participant.id}`,
                              !formik.values[participant.id]
                            )
                          }}
                          slot="prefix"
                        />
                      ) : (
                        <TtIcon
                          className="camera-off-icon"
                          library="one/webinars"
                          name="camera-off"
                          slot="prefix"
                        />
                      )}
                      {option.label}
                      {hasScreen && (
                        <TtButton
                          slot="suffix"
                          className="panel-participant-screen"
                          color={
                            formScreenValue === option.value
                              ? 'primary'
                              : 'secondary'
                          }
                          size="x-small"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleSelection(formik, option.value)
                          }}
                        >
                          <TtIcon library="one/webinars" name="screen-share" />
                        </TtButton>
                      )}
                    </TtMenuItem>
                  </div>
                )
              })}

              {(screen === 'tray' || lastActiveMediaTrayCardExists) && (
                <>
                  <TtMenuDivider />
                  <TtMenuItem onClick={() => handleTrayClick(formik)}>
                    <FormattedMessage defaultMessage="WebinarTray" />
                    <TtButton
                      slot="suffix"
                      className="panel-participant-tray"
                      color={
                        formik.values.screen === 'tray'
                          ? 'primary'
                          : 'secondary'
                      }
                      size="x-small"
                      onClick={() => handleTrayClick(formik)}
                    >
                      <TtIcon library="one/webinars" name="tray-mix-on" />
                    </TtButton>
                  </TtMenuItem>
                </>
              )}
            </TtMenu>
          </TtFlavorContext>
        )
      }}
    </TtFormik.Form>
  )
}

export { PanelMenuForm }
